/**
 * Elite Gate Utilities
 * Frontend Elite Gating ("Velvet Rope") - Patch D + D2
 *
 * Single source of truth for Elite eligibility checks.
 * Used by EliteGate component and API interceptors.
 *
 * Eligibility Rule:
 * eligible = active === true OR in_grace === true
 */

import { isEliteActive, isInGracePeriod, getEliteExpiryDate } from './entitlements';

/**
 * Check if user is eligible for Elite features
 * Eligible if: active OR in_grace (Option A: grace counts)
 *
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user can access Elite features
 */
export function isEliteEligible(user) {
  if (!user) return false;

  // Check entitlements structure from /api/auth/me
  const elite = user.entitlements?.elite;
  if (elite) {
    // Eligible if active OR in grace period
    return elite.active === true || elite.in_grace === true;
  }

  // Fallback to individual checks
  return isEliteActive(user) || isInGracePeriod(user);
}

/**
 * Get Elite eligibility status with details
 *
 * @param {Object} user - User object from auth context
 * @returns {Object} - { eligible, active, inGrace, expiresAt, graceUntil }
 */
export function getEliteEligibilityStatus(user) {
  if (!user) {
    return {
      eligible: false,
      active: false,
      inGrace: false,
      expiresAt: null,
      graceUntil: null,
      scheduledCancel: false,
    };
  }

  const elite = user.entitlements?.elite || {};
  const active = elite.active === true || isEliteActive(user);
  const inGrace = elite.in_grace === true || isInGracePeriod(user);
  const eligible = active || inGrace;

  return {
    eligible,
    active,
    inGrace,
    expiresAt: elite.expires_at ? new Date(elite.expires_at) : getEliteExpiryDate(user),
    graceUntil: elite.grace_until ? new Date(elite.grace_until) : null,
    scheduledCancel: elite.scheduled_cancel === true,
  };
}

/**
 * Get user-friendly message based on Elite status
 *
 * @param {Object} user - User object from auth context
 * @returns {Object} - { title, message, variant, showUpgrade }
 */
export function getEliteGateMessage(user) {
  const status = getEliteEligibilityStatus(user);

  if (status.eligible && status.inGrace) {
    // In grace period - show warning but allow access
    const graceDate = status.graceUntil || status.expiresAt;
    const graceStr = graceDate ? graceDate.toLocaleDateString() : 'soon';
    return {
      title: 'Payment Issue',
      message: `Your payment failed. Grace period active until ${graceStr}. Please update your payment method to avoid losing Elite access.`,
      variant: 'warning',
      showUpgrade: false,
      showUpdatePayment: true,
    };
  }

  if (!status.eligible) {
    return {
      title: 'Elite Feature',
      message: 'Upgrade to Elite to unlock this premium feature.',
      variant: 'default',
      showUpgrade: true,
      showUpdatePayment: false,
    };
  }

  if (status.scheduledCancel) {
    const expiryStr = status.expiresAt ? status.expiresAt.toLocaleDateString() : 'end of billing period';
    return {
      title: 'Elite Active',
      message: `Your Elite access will end on ${expiryStr}.`,
      variant: 'info',
      showUpgrade: false,
      showUpdatePayment: false,
    };
  }

  return {
    title: 'Elite Active',
    message: 'You have full access to this Elite feature.',
    variant: 'success',
    showUpgrade: false,
    showUpdatePayment: false,
  };
}

/**
 * Check if a 403 error is Elite-required
 *
 * @param {Error} error - Axios error object
 * @returns {boolean} - True if this is an Elite-gated 403
 */
export function isEliteRequiredError(error) {
  if (error?.response?.status !== 403) return false;

  // Check for X-Elite-Required header (case-insensitive)
  const headers = error.response.headers;
  const eliteRequired =
    headers?.['x-elite-required'] ||
    headers?.['X-Elite-Required'] ||
    headers?.get?.('x-elite-required');

  return eliteRequired === 'true' || eliteRequired === true;
}

/**
 * Global state for Elite upgrade modal
 * Can be subscribed to by any component
 */
let eliteUpgradeCallback = null;

export function setEliteUpgradeCallback(callback) {
  eliteUpgradeCallback = callback;
}

export function triggerEliteUpgradeModal(featureName = 'this feature') {
  if (eliteUpgradeCallback) {
    eliteUpgradeCallback(featureName);
    return true;
  }
  // Fallback: navigate to shop
  if (window.location.pathname !== '/shop') {
    window.location.href = '/shop?focus=elite&reason=required';
  }
  return false;
}

export default {
  isEliteEligible,
  getEliteEligibilityStatus,
  getEliteGateMessage,
  isEliteRequiredError,
  setEliteUpgradeCallback,
  triggerEliteUpgradeModal,
};
